import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Loader2, TrendingDown } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import BackOffice from "@/components/account/BackOffice";
import { PageHead, Pill, Section, TONES } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";

const TONE = TONES.insights;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const short = (iso) => {
    try { return new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: "numeric", day: "numeric" }); }
    catch { return iso; }
};
const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"], [365, "12 months"]];

function Tile({ label, value, note, testid }) {
    return (
        <div className="bg-white p-4" data-testid={testid}>
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-700">{label}</p>
            <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-zinc-900">{value}</p>
            {note && <p className="mt-1 text-xs text-zinc-600">{note}</p>}
        </div>
    );
}

function Bars({ rows, label, valueOf, max, testid }) {
    if (!rows.length) return <p className="text-sm text-zinc-600">Nothing yet.</p>;
    return (
        <div className="space-y-2" data-testid={testid}>
            {rows.map((r, i) => (
                <div key={i}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate text-zinc-900">{label(r)}</span>
                        <span className="shrink-0 font-mono tabular-nums text-zinc-900">{money(valueOf(r))}</span>
                    </div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-black/10">
                        <div className="h-full rounded-full" style={{
                            width: `${max ? Math.max((valueOf(r) / max) * 100, 2) : 0}%`, background: TONE,
                        }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

/** Recharts needs a real width — measure it ourselves instead of letting it guess at -1. */
function useWidth() {
    const ref = useRef(null);
    const [w, setW] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        setW(el.clientWidth);
        const ro = new ResizeObserver(([entry]) => setW(Math.round(entry.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, w];
}

export default function Analytics() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [chartRef, chartW] = useWidth();

    const load = useCallback(() => {
        setData(null);
        api.get("/back-office/analytics", { params: { days } }).then(({ data }) => setData(data))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not build your report"));
    }, [days]);
    useEffect(load, [load]);

    if (err) return (
        <BackOffice><div className="mx-auto max-w-2xl py-20 text-center" data-testid="analytics-locked">
            <BarChart3 size={34} className="mx-auto text-zinc-400" />
            <h1 className="mt-4 font-display text-2xl font-black text-zinc-900">Reports come with a space.</h1>
            <p className="mt-2 text-sm text-zinc-700">{err}</p>
            <Link to="/account/sell" className="mt-6 inline-block"><GreenButton data-testid="analytics-open-space">See the spaces</GreenButton></Link>
        </div></BackOffice>
    );

    const t = data?.tiles || {};
    const topMax = Math.max(...(data?.top_products || []).map((r) => r.revenue), 0);
    const cntMax = Math.max(...(data?.counters || []).map((r) => r.revenue), 0);
    const srcMax = Math.max(...(data?.sources || []).map((r) => r.revenue), 0);

    return (
        <BackOffice>
            <PageHead kicker="Insights" tone={TONE} title="Sales reports" testid="analytics-head"
                sub="What sold, what's sitting on the shelf, who came back and how they found your shop."
                right={
                    <div className="flex flex-wrap justify-end gap-1.5" data-testid="analytics-ranges">
                        {RANGES.map(([d, label]) => (
                            <Pill key={d} active={days === d} testid={`analytics-range-${d}`}
                                onClick={() => setDays(d)}>{label}</Pill>
                        ))}
                    </div>
                } />

            {!data ? (
                <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
            ) : (
                <div className="mt-5 space-y-2.5">
                    <div className="grid gap-px overflow-hidden border border-[#d0d0d0] bg-[#d0d0d0] sm:grid-cols-2 xl:grid-cols-4"
                        data-testid="analytics-tiles">
                        <Tile label={`Booked · last ${data.days} days`} value={money(t.booked)} testid="analytics-booked"
                            note={`${t.orders} order${t.orders === 1 ? "" : "s"} · ${t.units} piece${t.units === 1 ? "" : "s"}`} />
                        <Tile label="Paid" value={money(t.paid)} testid="analytics-paid"
                            note={`${money(t.unpaid)} still waiting on payment`} />
                        <Tile label="Average order" value={money(t.avg_order)} testid="analytics-avg" />
                        <Tile label="All time" value={money(t.all_time)} testid="analytics-alltime"
                            note={`${data.people.customers} customer${data.people.customers === 1 ? "" : "s"} · ${data.people.repeat_pct}% came back`} />
                    </div>

                    <Section title="Money by day" tone={TONE} testid="analytics-chart"
                        note={`last ${data.days} days`}>
                        <div ref={chartRef} className="h-56 w-full min-w-0">
                            {chartW > 0 && (
                                <BarChart width={chartW} height={224} data={data.by_day}
                                    margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                                    <CartesianGrid stroke="rgba(0,0,0,0.07)" vertical={false} />
                                    <XAxis dataKey="date" tickFormatter={short} tick={{ fontSize: 10, fill: "#4a4a4a" }}
                                        interval="preserveStartEnd" minTickGap={18} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 10, fill: "#4a4a4a" }} axisLine={false} tickLine={false} />
                                    <Tooltip formatter={(v) => money(v)} labelFormatter={short}
                                        contentStyle={{ border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, fontSize: 12 }} />
                                    <Bar dataKey="revenue" fill={TONE} radius={[2, 2, 0, 0]} />
                                </BarChart>
                            )}
                        </div>
                    </Section>

                    <div className="space-y-2.5">
                        <Section title="Best sellers" tone={TONE} testid="analytics-top"
                            note={`top ${(data.top_products || []).length} by money`}>
                            <Bars rows={data.top_products} max={topMax} testid="analytics-top-rows"
                                label={(r) => `${r.name} · ${r.units} pc`} valueOf={(r) => r.revenue} />
                        </Section>

                        <Section title="Sitting on the shelf" tone={TONE} testid="analytics-slow"
                            note={`${data.slow_total} listed product${data.slow_total === 1 ? "" : "s"} with no sale in this window`}>
                            {(data.slow_products || []).length ? (
                                <ul className="divide-y divide-black/[0.06]">
                                    {data.slow_products.map((p) => (
                                        <li key={p.slug} className="flex items-center gap-3 py-2 text-sm"
                                            data-testid={`analytics-slow-${p.slug}`}>
                                            <TrendingDown size={13} className="shrink-0 text-zinc-400" />
                                            <span className="min-w-0 flex-1 truncate text-zinc-900">{p.name}</span>
                                            <span className="shrink-0 font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-600">
                                                {p.days_ago != null ? `sold ${p.days_ago}d ago` : "never sold"}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : <p className="text-sm text-zinc-600">Everything you list has sold. Nice.</p>}
                        </Section>

                        <Section title="Which counter earns" tone={TONE} testid="analytics-counters">
                            <Bars rows={data.counters} max={cntMax} testid="analytics-counter-rows"
                                label={(r) => `${r.label} · ${r.units} pc`} valueOf={(r) => r.revenue} />
                        </Section>

                        <Section title="How they found you" tone={TONE} testid="analytics-sources"
                            note={`${data.coupon_orders} used a coupon · ${data.sale_orders} bought on sale`}>
                            <Bars rows={data.sources} max={srcMax} testid="analytics-source-rows"
                                label={(r) => `${r.label} · ${r.orders} order${r.orders === 1 ? "" : "s"}`}
                                valueOf={(r) => r.revenue} />
                        </Section>
                    </div>

                    <Section title="Who keeps coming back" tone={TONE} testid="analytics-people"
                        note={`${data.people.repeat} of ${data.people.customers} customers ordered more than once`}>
                        {(data.people.top || []).length ? (
                            <ul className="divide-y divide-black/[0.06]">
                                {data.people.top.map((p) => (
                                    <li key={p.email} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm"
                                        data-testid={`analytics-person-${p.email}`}>
                                        <span className="font-semibold text-zinc-900">{p.name || p.email}</span>
                                        <span className="font-mono text-[11px] text-zinc-600">{p.email}</span>
                                        <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-700">
                                            {p.orders} order{p.orders === 1 ? "" : "s"}
                                        </span>
                                        <span className="w-24 shrink-0 text-right font-mono tabular-nums text-zinc-900">{money(p.spent)}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : <p className="text-sm text-zinc-600">No orders yet.</p>}
                    </Section>
                </div>
            )}
        </BackOffice>
    );
}
